import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppConfig } from "../AppConfig";
import { PageRoutes } from "../constants/routes";
import { ErrorMessageManager } from "../core/http/ErrorMessageManager";
import { LocalStorageManager } from "../core/storage/LocalStorageManager";
import { CommonAsyncType } from "../core/types";
import { appActions } from "../ducks/app";
import { Campaign } from "../ducks/campaign";
import { AppliedCoupon, couponActions } from "../ducks/coupon";
import { Menu, menuActions, MenuInCart, menuSelectors } from "../ducks/menu";
import { orderActions, orderSelectors } from "../ducks/order";
import { Store } from "../ducks/store";
import { getDatePlusMinutes, isDateTimeBefore } from "../utils/date";
import { useApplySelectedAppCoupon } from "./useApplySelectedAppCoupon";
import { CouponHook } from "./useCoupon";
import { useNavigation } from "./useNavigation";

export type SelectedMenu = Menu & {
  selectedAmount: number;
  selectedOptionId?: number;
};

export const useCart = (
  store: Store | null,
  useCoupon: CouponHook,
  menuMapById?: Map<number, Menu> | null,
  recommendedMenus?: Menu[],
  limitedMenus?: Menu[],
) => {
  const dispatch = useDispatch();
  const { storeCode, date } = useParams<{ storeCode: string; date: string }>();
  const { navigateToPage } = useNavigation();

  const menusInCart = useSelector(menuSelectors.menusInCart);
  const orderContent = useSelector(orderSelectors.orderContent);
  const menusToReorder = useSelector(menuSelectors.menusToReorder);
  const campaign = LocalStorageManager.Instance.getObject<Campaign>(
    AppConfig.Instance.LocalStorageKey["campaign"],
  );

  const {
    selectedAppCoupons,
    appliedCoupons,
    totalCouponCodes,
    appCouponCodes,
    validateCoupon,
  } = useCoupon;

  const { handleApplySelectedAppCoupons } =
    useApplySelectedAppCoupon(useCoupon);

  const [showCartPopup, setShowCartPopup] = useState<boolean>(false);
  const [showCartOrderContent, setShowCartOrderContent] =
    useState<boolean>(false);
  const [showRecommendedMenuPopup, setShowRecommendedMenuPopup] =
    useState<boolean>(false);
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);

  useEffect(() => {
    dispatch(menuActions.getMenusFromCart.started({}));
  }, []);

  const recommendedMenusToShow = useMemo(() => {
    if (!recommendedMenus || !menusInCart) return [];

    const selectedMenuIds = menusInCart.map((menu) => menu.menuId);
    return recommendedMenus.filter(
      (menu) => !selectedMenuIds.includes(menu.id),
    );
  }, [recommendedMenus, menusInCart]);

  const selectedLimitedMenus = useMemo(() => {
    if (!limitedMenus || !menusInCart) return [];

    const selectedMenuIds = menusInCart
      .filter((menu) => menu.amount > 0)
      .map((menu) => menu.menuId);
    return limitedMenus.filter((menu) => selectedMenuIds.includes(menu.id));
  }, [limitedMenus, menusInCart]);

  const {
    orderedMenus,
    totalItemsInCart,
    totalPriceInCart,
    totalTaxInCart,
    orderCoupon,
  } = useMemo(() => {
    return {
      orderedMenus: orderContent.menus,
      totalItemsInCart: orderContent.totalQuantity,
      totalPriceInCart: orderContent.totalPriceWithTax,
      totalTaxInCart: orderContent.totalTaxAmount,
      orderCoupon: orderContent.coupon,
    };
  }, [orderContent]);

  const isCartEmpty = useMemo(() => {
    // if (selectedAppCoupon || appliedCoupons.length > 0) return false;

    if (!menusInCart) return true;

    return (
      menusInCart.length === 0 ||
      menusInCart.reduce((acc, cur) => acc + cur.amount, 0) === 0
    );
  }, [menusInCart, appliedCoupons, selectedAppCoupons]);

  const isNoItemsSelected = useMemo(() => {
    if (!menusInCart) return true;

    return menusInCart.length === 0;
  }, [menusInCart]);

  const showCart = useMemo(() => {
    return !isNoItemsSelected;
  }, [isNoItemsSelected]);

  useEffect(() => {
    if (isNoItemsSelected) setShowCartPopup(false);
  }, [isNoItemsSelected]);

  useEffect(() => {
    if (showCartPopup || showRecommendedMenuPopup) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [showCartPopup, showRecommendedMenuPopup]);

  const showAnimation = useCallback(() => {
    setShouldAnimate(true);
    setTimeout(() => {
      setShouldAnimate(false);
    }, 1000);
  }, []);

  const handleClickCartIndicator = useCallback(() => {
    if (!storeCode || !date) return;

    if (showRecommendedMenuPopup) {
      navigateToPage(
        PageRoutes.ORDER_CONFIRMATION.replace(":storeCode", storeCode).replace(
          ":date",
          date,
        ),
      );
      return;
    }

    if (showCartPopup) {
      if (recommendedMenusToShow && recommendedMenusToShow.length > 0) {
        setShowCartPopup(false);
        setShowRecommendedMenuPopup(true);
      } else {
        navigateToPage(
          PageRoutes.ORDER_CONFIRMATION.replace(
            ":storeCode",
            storeCode,
          ).replace(":date", date),
        );
      }
    } else {
      setShowCartPopup(true);
    }
  }, [
    showCartPopup,
    storeCode,
    date,
    showRecommendedMenuPopup,
    recommendedMenusToShow,
  ]);

  const handleCheckOrderItems = useCallback(
    (menus?: MenuInCart[], callback?: CommonAsyncType) => {
      if (!store || !date) return;

      if (!menusInCart) return;

      const { onComplete, onError, onSuccess } = callback ?? {};

      const isReservation = isDateTimeBefore(
        getDatePlusMinutes(new Date(), store.shortestTimeUntilPickup + 5),
        date,
      );

      const menusToCheck = menus ?? menusInCart;

      dispatch(
        orderActions.checkOrderItems.started({
          store_id: store.id,
          pickup_date: date,
          total_coupons: totalCouponCodes,
          menus: menusToCheck.map((menu) => ({
            id: menu.menuId,
            quantity: menu.amount,
            option: menu.optionId ? [menu.optionId] : [],
            coupon_code: menu.couponCode,
          })),
          is_reservation: isReservation,
          memo: "",
          early_bird: !!campaign ? 1 : undefined,
          onComplete,
          onError,
          onSuccess,
        }),
      );
    },
    [store, date, menusInCart, totalCouponCodes, appCouponCodes],
  );

  const handleRemoveMenusInCartNotSoldInStore = useCallback(
    (
      menus?: MenuInCart[],
      menuMap?: Map<number, Menu> | null,
      onComplete?: () => void,
    ) => {
      if (!menus || !menuMap) {
        return {
          hasError: true,
          success: [],
          error: [],
        };
      }

      const menusToCheck = menus;
      const menuKeyById = menuMap;

      const menusNotInStore = menusToCheck.filter(
        (menu) =>
          !menuKeyById.has(menu.menuId) ||
          (menuKeyById.get(menu.menuId)?.isPreorder &&
            !menuKeyById.get(menu.menuId)?.isEnableButton),
      );
      const newMenus = menusToCheck.filter((menu) => {
        return (
          menuKeyById.has(menu.menuId) &&
          !menuKeyById.get(menu.menuId)?.isPreorder &&
          menuKeyById.get(menu.menuId)?.isEnableButton
        );
      });

      if (menus !== undefined) {
        return {
          hasError: menusNotInStore.length > 0,
          success: newMenus,
          error: menusNotInStore,
        };
      }

      if (menusNotInStore.length > 0) {
        dispatch(menuActions.setMenuInCart.started({ menus: newMenus }));
        dispatch(
          appActions.showModal.started({
            title: ErrorMessageManager.Instance.getErrorMessage("CART-0001"),
            subTitle: `${menusNotInStore.map((menu) => `${menu.menuName}${menu.optionName ? `(${menu.optionName})` : ""}`).join("\n")}`,
            nextButtonLabel: "閉じる",
            handleNext: () => {
              dispatch(appActions.closeModal());
              setTimeout(() => {
                onComplete?.();
              }, 100);
            },
          }),
        );
      } else {
        onComplete?.();
      }

      return {
        hasError: menusNotInStore.length > 0,
        success: newMenus,
        error: menusNotInStore,
      };
    },
    [],
  );

  const handleMenusToReorder = useCallback(() => {
    if (!menusToReorder) return;

    const { hasError, success, error } = handleRemoveMenusInCartNotSoldInStore(
      menusToReorder,
      menuMapById,
    );

    if (hasError) {
      dispatch(
        appActions.showModal.started({
          title: "下記の全てのメニューが見つからないため削除されました。",
          subTitle: `${error
            .map(
              (menu) =>
                `${menu.menuName}${menu.optionName ? `(${menu.optionName})` : ""}`,
            )
            .join("\n")}`,
          nextButtonLabel: success.length === 0 ? "履歴に戻る" : undefined,
          handleNext: () => {
            navigateToPage(PageRoutes.ORDER_HISTORY);
            dispatch(appActions.closeModal());
          },
          closeButtonLabel: success.length === 0 ? "注文を続ける" : "閉じる",
          handleClose: () => {
            dispatch(appActions.closeModal());

            if (success.length > 0) {
              dispatch(appActions.showLoading());
              handleCheckOrderItems(success, {
                onComplete: () => dispatch(appActions.hideLoading()),
                onSuccess: () =>
                  dispatch(
                    menuActions.setMenuInCart.started({
                      menus: success,
                    }),
                  ),
              });
            }
          },
        }),
      );
    } else {
      dispatch(appActions.showLoading());
      handleCheckOrderItems(success, {
        onComplete: () => dispatch(appActions.hideLoading()),
        onSuccess: () =>
          dispatch(
            menuActions.setMenuInCart.started({
              menus: success,
              onSuccess: () =>
                dispatch(
                  appActions.showModal.started({
                    title: "全てのメニューがカートに追加されました。",
                    closeButtonLabel: "閉じる",
                    handleClose: () => {
                      dispatch(appActions.closeModal());
                    },
                  }),
                ),
            }),
          ),
      });
    }
  }, [menuMapById, menusToReorder]);

  const handleRemoveCouponWithMenuQuantityZero = useCallback(
    (appliedCoupons?: AppliedCoupon[], menusInCart?: MenuInCart[]) => {
      if (!appliedCoupons || appliedCoupons.length === 0) return;
      const couponWithoutTotalCoupons = appliedCoupons.filter(
        (coupon) => !coupon.isTotalCoupon,
      );
      // If there are no menus in the cart or applied coupons, remove all applied coupons except total coupons
      if (
        (!menusInCart || menusInCart.length === 0) &&
        couponWithoutTotalCoupons.length > 0
      ) {
        dispatch(
          couponActions.removeAppliedCoupon.started({
            couponUuids: couponWithoutTotalCoupons.map((coupon) => coupon.uuid),
          }),
        );
        return;
      }

      const associatedCouponUuids = new Set(
        (menusInCart ?? [])
          .filter((menu) => !!menu.couponUuid)
          .map((menu) => menu.couponUuid),
      );

      const couponUuidsToRemove = couponWithoutTotalCoupons
        .filter((coupon) => !associatedCouponUuids.has(coupon.uuid))
        .map((coupon) => coupon.uuid);

      if (couponUuidsToRemove.length > 0) {
        dispatch(
          couponActions.removeAppliedCoupon.started({
            couponUuids: couponUuidsToRemove,
          }),
        );
      }
    },
    [],
  );

  const handleCheckCart = useCallback(() => {
    if (!store || !date) return;
    if (!menusInCart || !menuMapById) return;
    if (selectedAppCoupons && selectedAppCoupons.length > 0) {
      handleApplySelectedAppCoupons({
        store,
        storePickupDate: date,
        selectedAppCoupons,
        appliedCoupons,
        menusInCart,
      });
    } else if (menusToReorder && menusToReorder.length > 0) {
      dispatch(menuActions.cancelReorder());
      handleMenusToReorder();
    } else if (appliedCoupons && appliedCoupons.length > 0) {
      validateCoupon(store.id, date, store.shortestTimeUntilPickup, () => {
        if (menusInCart.length === 0) return;

        const { hasError } = handleRemoveMenusInCartNotSoldInStore(
          menusInCart,
          menuMapById,
        );
        if (!hasError) handleCheckOrderItems();
      });
    } else if (menusInCart.length > 0) {
      const { hasError } = handleRemoveMenusInCartNotSoldInStore(
        menusInCart,
        menuMapById,
      );
      if (!hasError) handleCheckOrderItems();
    }
  }, [
    store,
    date,
    menusInCart,
    menusToReorder,
    selectedAppCoupons,
    appliedCoupons,
    handleMenusToReorder,
    handleCheckOrderItems,
    handleRemoveMenusInCartNotSoldInStore,
    handleApplySelectedAppCoupons,
  ]);

  useEffect(() => {
    if (!store || !menuMapById) return;
    handleRemoveCouponWithMenuQuantityZero(
      appliedCoupons ?? [],
      menusInCart ?? [],
    );
    if (isCartEmpty && !menusToReorder) {
      dispatch(orderActions.clearOrderItems());

      if (!selectedAppCoupons || selectedAppCoupons.length === 0) return;
    }

    handleCheckCart();
  }, [
    menusInCart,
    isCartEmpty,
    menuMapById,
    // selectedAppCoupons,
    menusToReorder,
  ]);

  const handleAddItemToCart = useCallback(
    (args: {
      menuId: number;
      menuName: string;
      amount: number;
      isEarlyBird: boolean;
      optionId?: number;
      optionName?: string;
    }) => {
      const { menuId, menuName, amount, optionId, optionName, isEarlyBird } =
        args;
      if (amount === 0) return;

      // if in early bird campaign and menu is not discount, show modal
      if (!!campaign && !menuMapById?.get(menuId)?.campaign) {
        dispatch(
          appActions.showModal.started({
            title: "このメニューには割引がありません。",
            subTitle: "選択したメニューには適用可能な割引がありません。",
            nextButtonLabel: "閉じる",
          }),
        );
        return;
      }

      dispatch(
        menuActions.addMenuToCart.started({
          menuId,
          menuName,
          amount,
          optionId,
          optionName,
          isEarlyBird,
        }),
      );

      showAnimation();
    },
    [campaign, menuMapById],
  );

  const handleUpdateItem = useCallback(
    (
      index: number,
      newAmount: number,
      newOptionId?: number,
      newOptionName?: string,
    ) => {
      if (!menusInCart) return;

      const newMenus = menusInCart.map((menu, i) => {
        if (i === index) {
          return {
            ...menu,
            amount: newAmount,
            optionId: newOptionId,
            optionName: newOptionName,
          };
        }

        return menu;
      });

      dispatch(menuActions.setMenuInCart.started({ menus: newMenus }));

      showAnimation();
    },
    [menusInCart],
  );

  const handleRemoveItem = useCallback((index: number) => {
    dispatch(menuActions.removeMenuFromCart.started({ index }));
  }, []);

  return {
    menusInCart,
    orderedMenus,
    totalItemsInCart,
    totalPriceInCart,
    totalTaxInCart,
    orderCoupon,
    showCart,
    shouldAnimate,
    showCartPopup,
    setShowCartPopup,
    selectedLimitedMenus,
    recommendedMenusToShow,
    showRecommendedMenuPopup,
    setShowRecommendedMenuPopup,
    handleClickCartIndicator,
    showCartOrderContent,
    setShowCartOrderContent,
    handleAddItemToCart,
    handleUpdateItem,
    handleRemoveItem,
  };
};
